import { Box, Button, IconButton, Typography } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FTimePicker } from '../../../../../../../components/baseV2';
import TrashIcon from '../../../../../../../components/svgs/TrashIcon';
import { FORTA_BLUE, FORTA_ERROR } from '../../../../../../../utils/colors';
import {
  GOAL_TIMING_ADD_BUTTON_SX,
  GOAL_TIMING_FIELD_ROOT_SX,
  GOAL_TIMING_INPUTS_SX,
  GOAL_TIMING_NOW_BUTTON_SX,
  GOAL_TIMING_SELECTED_ITEM_SX,
  GOAL_TIMING_SELECTED_LIST_SX,
  TECHNIQUE_DELETE_BUTTON_SX,
} from './styles';
import {
  buildSessionTime,
  buildTiming,
  getDisabledGoalMeridiem,
  hasGoalTimingOverlap,
  isGoalTimeDisabled,
  resolveGoalTimeChange,
} from './utils';

import type { Moment } from 'moment';
import type { ISessionGoalTiming } from '../../../../../../../utils/types';
import type { GoalTimingFieldProps } from './types';
import type { FTimePickerChangeContext, FTimePickerProps } from '../../../../../../../components/baseV2';

export default function GoalTimingField({
  disabled,
  isBehaviorReductionGoal,
  sessionStart,
  expectedDuration,
  timings,
  showError,
  onChange,
}: GoalTimingFieldProps) {
  const { t } = useTranslation('sessionNotes');
  const [draftStart, setDraftStart] = useState<Moment | null>(null);
  const [draftEnd, setDraftEnd] = useState<Moment | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [currentTime, setCurrentTime] = useState(() => moment());

  useEffect(() => {
    const intervalId = window.setInterval(() => setCurrentTime(moment()), 30000);
    return () => window.clearInterval(intervalId);
  }, []);

  const sessionEnd =
    sessionStart && expectedDuration != null
      ? sessionStart.clone().add(expectedDuration, 'seconds')
      : null;
  const canUseNow =
    !disabled &&
    Boolean(sessionStart) &&
    Boolean(sessionEnd) &&
    currentTime.isBetween(sessionStart, sessionEnd, undefined, '[]');
  const hasTimingError = showError && !isBehaviorReductionGoal && timings.length === 0;
  const draftTiming = buildTiming(draftStart, draftEnd, sessionStart);
  const draftIsValid =
    Boolean(draftTiming) &&
    draftTiming!.start_offset_seconds >= 0 &&
    draftTiming!.end_offset_seconds <= (expectedDuration ?? -1) &&
    draftTiming!.end_offset_seconds > draftTiming!.start_offset_seconds;
  const hasDraftOverlap = draftIsValid && hasGoalTimingOverlap(draftTiming, timings, editingIndex);
  const canSaveDraft = !disabled && !isBehaviorReductionGoal && draftIsValid && !hasDraftOverlap;

  const shouldDisableGoalTime: FTimePickerProps['shouldDisableTime'] = (value, view) =>
    isGoalTimeDisabled(value, view, sessionStart, expectedDuration);
  const disabledMeridiem = getDisabledGoalMeridiem(sessionStart, expectedDuration);

  const handleTimeChange = (
    field: 'start' | 'end',
    value: Moment | null,
    context?: FTimePickerChangeContext,
  ) => {
    const resolvedTime = resolveGoalTimeChange(value, sessionStart, expectedDuration, context);
    if (field === 'start') {
      setDraftStart(resolvedTime);
    } else {
      setDraftEnd(resolvedTime);
    }
  };

  const handleNow = (field: 'start' | 'end') => {
    if (!canUseNow) return;
    handleTimeChange(field, moment());
  };

  const handleSaveDraft = () => {
    if (!canSaveDraft || !draftTiming) return;

    const nextTimings = [...timings];
    if (editingIndex == null) {
      nextTimings.push(draftTiming);
    } else {
      nextTimings[editingIndex] = draftTiming;
    }
    onChange(nextTimings);
    setDraftStart(null);
    setDraftEnd(null);
    setEditingIndex(null);
  };

  const handleEdit = (index: number, timing: ISessionGoalTiming) => {
    if (!sessionStart) return;
    setDraftStart(buildSessionTime(sessionStart, timing.start_offset_seconds));
    setDraftEnd(buildSessionTime(sessionStart, timing.end_offset_seconds));
    setEditingIndex(index);
  };

  const handleDelete = (index: number) => {
    onChange(timings.filter((_timing, timingIndex) => timingIndex !== index));
    if (editingIndex === index) {
      setDraftStart(null);
      setDraftEnd(null);
      setEditingIndex(null);
    }
  };

  const formatTiming = (timing: ISessionGoalTiming) => {
    if (!sessionStart) return '';
    const startTime = buildSessionTime(sessionStart, timing.start_offset_seconds);
    const endTime = buildSessionTime(sessionStart, timing.end_offset_seconds);
    if (!startTime || !endTime) return '';
    return `${startTime.format('h:mm A')} - ${endTime.format('h:mm A')}`;
  };

  return (
    <Box sx={GOAL_TIMING_FIELD_ROOT_SX}>
      {!isBehaviorReductionGoal && (
        <>
          <Box sx={GOAL_TIMING_INPUTS_SX}>
            <FTimePicker
              label={t('goalsPracticed.startTime')}
              required
              fullWidth
              format="h:mm A"
              showClockIcon
              use12Hours
              allowClear
              disabled={disabled || !sessionStart || expectedDuration == null}
              referenceDate={sessionStart}
              shouldDisableTime={shouldDisableGoalTime}
              disabledMeridiem={disabledMeridiem}
              skipDisabled
              value={draftStart}
              placeholder={t('goalsPracticed.selectStartTime')}
              error={hasDraftOverlap || (hasTimingError && !draftStart)}
              onChange={(value, context) => handleTimeChange('start', value, context)}
            />
            <Button
              sx={GOAL_TIMING_NOW_BUTTON_SX}
              disabled={!canUseNow}
              onClick={() => handleNow('start')}
            >
              {t('goalsPracticed.now')}
            </Button>
            <FTimePicker
              label={t('goalsPracticed.endTime')}
              required
              fullWidth
              format="h:mm A"
              showClockIcon
              use12Hours
              allowClear
              disabled={disabled || !sessionStart || expectedDuration == null}
              referenceDate={sessionStart}
              shouldDisableTime={shouldDisableGoalTime}
              disabledMeridiem={disabledMeridiem}
              skipDisabled
              value={draftEnd}
              placeholder={t('goalsPracticed.selectEndTime')}
              error={hasDraftOverlap || (hasTimingError && !draftEnd)}
              onChange={(value, context) => handleTimeChange('end', value, context)}
            />
            <Button
              sx={GOAL_TIMING_NOW_BUTTON_SX}
              disabled={!canUseNow}
              onClick={() => handleNow('end')}
            >
              {t('goalsPracticed.now')}
            </Button>
          </Box>
          {hasTimingError && (
            <Typography color={FORTA_ERROR} fontSize={12} mt={0.5}>
              {t('goalsPracticed.goalTimingRequired')}
            </Typography>
          )}
          {hasDraftOverlap && (
            <Typography role="alert" color={FORTA_ERROR} fontSize={12} mt={0.5}>
              {t('goalsPracticed.goalTimingOverlap')}
            </Typography>
          )}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              sx={GOAL_TIMING_ADD_BUTTON_SX}
              disabled={!canSaveDraft}
              onClick={handleSaveDraft}
            >
              {editingIndex != null
                ? t('goalsPracticed.updateTime')
                : timings.length > 0
                  ? t('goalsPracticed.practicedAgain')
                  : t('goalsPracticed.addTime')}
            </Button>
          </Box>
        </>
      )}
      {timings.length > 0 && (
        <Box sx={GOAL_TIMING_SELECTED_LIST_SX}>
          {timings.map((timing, index) => (
            <Box
              key={`${timing.start_offset_seconds}-${timing.end_offset_seconds}-${index}`}
              sx={GOAL_TIMING_SELECTED_ITEM_SX}
            >
              <Typography color={FORTA_BLUE.primary}>{formatTiming(timing)}</Typography>
              {!disabled && !isBehaviorReductionGoal && (
                <Box>
                  <IconButton
                    aria-label={t('goalsPracticed.editGoalTiming')}
                    sx={TECHNIQUE_DELETE_BUTTON_SX}
                    onClick={() => handleEdit(index, timing)}
                  >
                    <EditOutlinedIcon sx={{ color: FORTA_BLUE.primary, width: 20, height: 20 }} />
                  </IconButton>
                  <IconButton
                    aria-label={t('goalsPracticed.deleteGoalTiming')}
                    sx={TECHNIQUE_DELETE_BUTTON_SX}
                    onClick={() => handleDelete(index)}
                  >
                    <TrashIcon color={FORTA_BLUE.primary} width={18} height={18} />
                  </IconButton>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
